
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Shield, Users, Star, Trophy, Award, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const CommunityLeaderBadge: React.FC = () => {
  const { toast } = useToast();
  const [leaderStats, setLeaderStats] = useState({
    badgeLevel: 'Gold',
    communityPoints: 2450,
    membersHelped: 127,
    postsShared: 89,
    eventsOrganized: 12,
    mentoringSessions: 34
  });

  const claimReward = () => {
    toast({
      title: "Reward Claimed!",
      description: "Your community leadership points have been added to your profile."
    });
  };

  const shareAchievement = () => {
    toast({
      title: "Achievement Shared!",
      description: "Your community leader badge has been shared to your profile."
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            Community Leader Badge & Recognition
          </CardTitle>
          <CardDescription>
            Showcase your leadership and contribution to the TeenVisor community.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Badge Status */}
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Crown className="h-16 w-16 text-white" />
              </div>
              <Badge className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white">
                {leaderStats.badgeLevel} Leader
              </Badge>
            </div>
            <h3 className="text-2xl font-bold mt-4">Community Leader</h3>
            <p className="text-muted-foreground">Recognized for exceptional community contributions</p>
          </div>

          {/* Leadership Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-4 text-center">
                <Star className="h-6 w-6 mx-auto mb-2 text-yellow-600" />
                <div className="text-2xl font-bold text-yellow-700">{leaderStats.communityPoints}</div>
                <p className="text-sm text-yellow-600">Leadership Points</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold text-green-700">{leaderStats.membersHelped}</div>
                <p className="text-sm text-green-600">Members Helped</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 text-center">
                <Shield className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold text-blue-700">{leaderStats.postsShared}</div>
                <p className="text-sm text-blue-600">Posts Shared</p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4 text-center">
                <Trophy className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold text-purple-700">{leaderStats.eventsOrganized}</div>
                <p className="text-sm text-purple-600">Events Organized</p>
              </CardContent>
            </Card>
            
            <Card className="border-indigo-200 bg-indigo-50">
              <CardContent className="p-4 text-center">
                <Award className="h-6 w-6 mx-auto mb-2 text-indigo-600" />
                <div className="text-2xl font-bold text-indigo-700">{leaderStats.mentoringSessions}</div>
                <p className="text-sm text-indigo-600">Mentoring Sessions</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 text-center">
                <Zap className="h-6 w-6 mx-auto mb-2 text-orange-600" />
                <div className="text-2xl font-bold text-orange-700">Gold</div>
                <p className="text-sm text-orange-600">Current Level</p>
              </CardContent>
            </Card>
          </div>

          {/* Leadership Perks */}
          <div>
            <h4 className="font-semibold mb-3">Leadership Perks & Benefits</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded">
                <Crown className="h-4 w-4 text-yellow-600" />
                <span className="text-sm">Priority access to new features</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                <Shield className="h-4 w-4 text-blue-600" />
                <span className="text-sm">Special recognition in community</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                <Users className="h-4 w-4 text-green-600" />
                <span className="text-sm">Direct line to TeenVisor team</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                <Trophy className="h-4 w-4 text-purple-600" />
                <span className="text-sm">Exclusive leader-only events</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={claimReward} className="bg-yellow-500 hover:bg-yellow-600">
              <Award className="h-4 w-4 mr-2" />
              Claim Weekly Rewards
            </Button>
            <Button variant="outline" onClick={shareAchievement}>
              <Star className="h-4 w-4 mr-2" />
              Share Achievement
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunityLeaderBadge;
